import { useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { login as loginApi, fetchMe } from "@/api/auth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { useAuthStore } from "@/store/auth";
import { decodeJwt, useTenantStore } from "@/store/tenant";

interface LocationState {
  from?: { pathname: string };
}

export function LoginPage() {
  const [email, setEmail] = useState("admin@sview360.local");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setUser = useAuthStore((s) => s.setUser);
  const setAuthChecked = useAuthStore((s) => s.setAuthChecked);
  const navigate = useNavigate();
  const location = useLocation();
  const from =
    ((location.state as LocationState | null)?.from?.pathname) ?? "/cameras";

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await loginApi(email, password);
      if (res.requires_mfa) {
        setError(
          "MFA required — TOTP entry is not implemented yet in this build.",
        );
        return;
      }
      // S-SEC-3 — the API has set httpOnly auth cookies; we do NOT store
      // the tokens in JS. The body still carries the access token during
      // the transition, which we use transiently (never persisted) to
      // populate the tenant store. Identity comes from /me.
      if (!res.access_token) {
        setError("Login failed — no session established");
        return;
      }
      const me = await fetchMe();
      setUser(me);
      setAuthChecked(true);
      // S2 — decode the JWT once and populate the tenant store so
      // every other page can branch on isSuperAdmin / tenant_id
      // without re-decoding. Transient: the token is not stored.
      const claims = decodeJwt(res.access_token);
      if (claims) {
        useTenantStore.getState().setIsSuperAdmin(claims.is_super_admin);
        useTenantStore
          .getState()
          .setTenant(
            claims.tenant_id,
            me.full_name ?? me.email,
            (claims as { plan?: string }).plan ?? "",
          );
      }
      navigate(from, { replace: true });
    } catch (err) {
      const msg =
        (err as { response?: { data?: { detail?: string } } }).response?.data
          ?.detail ?? "Login failed";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Sign in to SView360</CardTitle>
          <CardDescription>
            STQC-certified Cloud VMS — banking edition.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={onSubmit}>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
